import {
  COLORS,
  UNITS,
  WORKTOPS,
  RANGES,
  FLOORING,
  SINKS,
  TAPS,
  STORAGE,
  CABINET,
} from './cloudinaryImages'
import {
  RANGE_PRICES,
  PREBUILT_RANGES_PRICES,
  HANDLELESS_RANGE_PRICES,
  WORKTOP_PRICES,
  APPLIANCES_PRICES,
  INSTALLATION_PRICES,
  FLOORING_PRICES,
  APPLIANCES_NAME_VALUES_SMEG_EXCEPTION,
  APPLIANCES_NAME_VALUES,
  SINKS_TYPE_VALUES,
  SINK_PRICES,
  HANDLES_TYPE_VALUES_WITH,
  HANDLES_TYPE_VALUES_WITHOUT,
  HANDLES_TYPE_VALUES_COMBINED,
} from './prices'

const sections = [
  {
    id: 'units',
    title: 'How many kitchen units do you need?',
    shortTitle: 'Units',
    type: 'units',
    description:
      'Select your approximate number of units – price includes panels and plinths.',
    tooltip: {
      title: 'What do we mean by kitchen units?',
      description: [
        'A unit is a cabinet, either a base or a wall cabinet.',
        '• Small kitchen: around 8 to 10 units, ideal for galley or compact spaces.',
        '• Medium kitchen: around 12 to 15 units, perfect for L-shaped or U-shaped layouts.',
        '• Large kitchen: around 16 to 20 units, suited to open-plan layouts with an island.',
        '• Extra-large kitchen: 25 units or more, designed for generous spaces with multiple zones or extended cabinetry runs.',
        'Your estimate includes everything you need — cabinets, panels, plinths and trims — so you can plan with confidence.',
      ],
    },
    cards: [
      {
        type: 'small',
        title: 'Small',
        units: 8,
        description: 'For kitchens around 10m²',
        price: 0,
        image: UNITS.small,
      },
      {
        type: 'medium',
        title: 'Medium',
        units: 12,
        description: 'For kitchens around 15m²',
        price: 0,
        image: UNITS.medium,
      },
      {
        type: 'large',
        title: 'Large',
        units: 16,
        description: 'For kitchens around 20m²',
        price: 0,
        image: UNITS.large,
      },
      {
        type: 'xlarge',
        title: 'Extra large',
        units: 20,
        description: 'For kitchens around 25m²',
        price: 0,
        image: UNITS.xlarge,
      },
    ],
  },
  {
    id: 'range',
    type: 'range',
    title: 'Which kitchen range do you prefer?',
    shortTitle: 'Range',
    description: 'Choose your dream kitchen range from the list below.',
    tooltip: {
      title: 'Which kitchen range do you prefer?',
      description: [
        'Simply, select the kitchen range you love the most for your space. And don’t worry about colour for now – that won’t have any impact on price.',
        'Please note, our whole range of kitchens are available on finance and we’ll show you the approximate Pay Monthly cost in your quote, as well as the total cost.',
      ],
    },
    cards: [
      {
        title: 'Ascoli',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'A Modern style kitchen featuring flat, slab-fronted doors in a smooth, matt wood or painted-effect finish.',
        priceBand: 1,
        colors: COLORS.ascoli,
        prices: {
          small: RANGE_PRICES.ascoli[0],
          medium: RANGE_PRICES.ascoli[1],
          large: RANGE_PRICES.ascoli[2],
          xlarge: RANGE_PRICES.ascoli[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.ascoli[0],
          medium: PREBUILT_RANGES_PRICES.ascoli[1],
          large: PREBUILT_RANGES_PRICES.ascoli[2],
          xlarge: PREBUILT_RANGES_PRICES.ascoli[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.ascoli[0],
          medium: HANDLELESS_RANGE_PRICES.ascoli[1],
          large: HANDLELESS_RANGE_PRICES.ascoli[2],
          xlarge: HANDLELESS_RANGE_PRICES.ascoli[3],
        },
        image: RANGES.ascoli,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Tatton',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Traditional Shaker style doors in a contemporary design, featuring a smooth, matt-effect finish with style.',
        priceBand: 1,
        colors: COLORS.tatton,
        prices: {
          small: RANGE_PRICES.tatton[0],
          medium: RANGE_PRICES.tatton[1],
          large: RANGE_PRICES.tatton[2],
          xlarge: RANGE_PRICES.tatton[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.tatton[0],
          medium: PREBUILT_RANGES_PRICES.tatton[1],
          large: PREBUILT_RANGES_PRICES.tatton[2],
          xlarge: PREBUILT_RANGES_PRICES.tatton[3],
        },
        image: RANGES.tatton,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_WITH,
      },
      {
        title: 'Nova',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'A Modern style kitchen featuring flat, slab-fronted doors in a contemporary design with matt or gloss finish.',
        priceBand: 1,
        colors: COLORS.nova,
        prices: {
          small: RANGE_PRICES.nova[0],
          medium: RANGE_PRICES.nova[1],
          large: RANGE_PRICES.nova[2],
          xlarge: RANGE_PRICES.nova[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.nova[0],
          medium: PREBUILT_RANGES_PRICES.nova[1],
          large: PREBUILT_RANGES_PRICES.nova[2],
          xlarge: PREBUILT_RANGES_PRICES.nova[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.nova[0],
          medium: HANDLELESS_RANGE_PRICES.nova[1],
          large: HANDLELESS_RANGE_PRICES.nova[2],
          xlarge: HANDLELESS_RANGE_PRICES.nova[3],
        },
        image: RANGES.nova,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Luna',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Our most popular kitchen with integrated J-pull handles and a sleek Handleless look in gloss and matt finish.',
        priceBand: 2,
        colors: COLORS.luna,
        prices: {
          small: HANDLELESS_RANGE_PRICES.luna[0],
          medium: HANDLELESS_RANGE_PRICES.luna[1],
          large: HANDLELESS_RANGE_PRICES.luna[2],
          xlarge: HANDLELESS_RANGE_PRICES.luna[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.luna[0],
          medium: PREBUILT_RANGES_PRICES.luna[1],
          large: PREBUILT_RANGES_PRICES.luna[2],
          xlarge: PREBUILT_RANGES_PRICES.luna[3],
        },
        image: RANGES.luna,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_WITHOUT,
      },
      {
        title: 'Ambleside',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'With a woodgrain texture that’s perfectly offset by a choice of five versatile colours.',
        priceBand: 2,
        colors: COLORS.ambleside,
        prices: {
          small: RANGE_PRICES.ambleside[0],
          medium: RANGE_PRICES.ambleside[1],
          large: RANGE_PRICES.ambleside[2],
          xlarge: RANGE_PRICES.ambleside[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.ambleside[0],
          medium: PREBUILT_RANGES_PRICES.ambleside[1],
          large: PREBUILT_RANGES_PRICES.ambleside[2],
          xlarge: PREBUILT_RANGES_PRICES.ambleside[3],
        },
        image: RANGES.ambleside,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_WITH,
      },
      {
        title: 'Brunswick',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'A modern slim-frame Shaker kitchen with a super-matt, anti-fingerprint finish, offering a sleek & durable design.',
        priceBand: 2,
        colors: COLORS.brunswick,
        prices: {
          small: RANGE_PRICES.brunswick[0],
          medium: RANGE_PRICES.brunswick[1],
          large: RANGE_PRICES.brunswick[2],
          xlarge: RANGE_PRICES.brunswick[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.brunswick[0],
          medium: PREBUILT_RANGES_PRICES.brunswick[1],
          large: PREBUILT_RANGES_PRICES.brunswick[2],
          xlarge: PREBUILT_RANGES_PRICES.brunswick[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.brunswick[0],
          medium: HANDLELESS_RANGE_PRICES.brunswick[1],
          large: HANDLELESS_RANGE_PRICES.brunswick[2],
          xlarge: HANDLELESS_RANGE_PRICES.brunswick[3],
        },
        image: RANGES.brunswick,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Hoxton',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Smooth, slab doors in a painted-effect finish in a contemporary, minimalist style.',
        priceBand: 2,
        colors: COLORS.hoxton,
        prices: {
          small: RANGE_PRICES.hoxton[0],
          medium: RANGE_PRICES.hoxton[1],
          large: RANGE_PRICES.hoxton[2],
          xlarge: RANGE_PRICES.hoxton[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.hoxton[0],
          medium: PREBUILT_RANGES_PRICES.hoxton[1],
          large: PREBUILT_RANGES_PRICES.hoxton[2],
          xlarge: PREBUILT_RANGES_PRICES.hoxton[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.hoxton[0],
          medium: HANDLELESS_RANGE_PRICES.hoxton[1],
          large: HANDLELESS_RANGE_PRICES.hoxton[2],
          xlarge: HANDLELESS_RANGE_PRICES.hoxton[3],
        },
        image: RANGES.hoxton,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Portobello',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Portobello pulls together contemporary clean lines and organic woodgrain texture.',
        priceBand: 2,
        colors: COLORS.portobello,
        prices: {
          small: RANGE_PRICES.portobello[0],
          medium: RANGE_PRICES.portobello[1],
          large: RANGE_PRICES.portobello[2],
          xlarge: RANGE_PRICES.portobello[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.portobello[0],
          medium: PREBUILT_RANGES_PRICES.portobello[1],
          large: PREBUILT_RANGES_PRICES.portobello[2],
          xlarge: PREBUILT_RANGES_PRICES.portobello[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.portobello[0],
          medium: HANDLELESS_RANGE_PRICES.portobello[1],
          large: HANDLELESS_RANGE_PRICES.portobello[2],
          xlarge: HANDLELESS_RANGE_PRICES.portobello[3],
        },
        image: RANGES.portobello,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Duxbury',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Expertly crafted J-pull handles designed to stand the test of time, with a colour-matched reverse.',
        priceBand: 2,
        colors: COLORS.duxbury,
        prices: {
          small: HANDLELESS_RANGE_PRICES.duxbury[0],
          medium: HANDLELESS_RANGE_PRICES.duxbury[1],
          large: HANDLELESS_RANGE_PRICES.duxbury[2],
          xlarge: HANDLELESS_RANGE_PRICES.duxbury[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.duxbury[0],
          medium: PREBUILT_RANGES_PRICES.duxbury[1],
          large: PREBUILT_RANGES_PRICES.duxbury[2],
          xlarge: PREBUILT_RANGES_PRICES.duxbury[3],
        },
        image: RANGES.duxbury,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_WITHOUT,
      },
      {
        title: 'Soho',
        collection: 'Paint To Order',
        ifcAvailable: true,
        description:
          'A premium Modern style design with bold, slab-fronted doors painted in a matt finish and available in 21 colours.',
        priceBand: 3,
        colors: COLORS.soho,
        prices: {
          small: RANGE_PRICES.soho[0],
          medium: RANGE_PRICES.soho[1],
          large: RANGE_PRICES.soho[2],
          xlarge: RANGE_PRICES.soho[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.soho[0],
          medium: PREBUILT_RANGES_PRICES.soho[1],
          large: PREBUILT_RANGES_PRICES.soho[2],
          xlarge: PREBUILT_RANGES_PRICES.soho[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.soho[0],
          medium: HANDLELESS_RANGE_PRICES.soho[1],
          large: HANDLELESS_RANGE_PRICES.soho[2],
          xlarge: HANDLELESS_RANGE_PRICES.soho[3],
        },
        image: RANGES.soho,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Dunham',
        collection: 'Uniquely',
        ifcAvailable: true,
        description:
          'A contemporary Shaker style design in a velvety matt painted finish featuring sleek panelled doors.',
        priceBand: 3,
        colors: COLORS.dunham,
        prices: {
          small: RANGE_PRICES.dunham[0],
          medium: RANGE_PRICES.dunham[1],
          large: RANGE_PRICES.dunham[2],
          xlarge: RANGE_PRICES.dunham[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.dunham[0],
          medium: PREBUILT_RANGES_PRICES.dunham[1],
          large: PREBUILT_RANGES_PRICES.dunham[2],
          xlarge: PREBUILT_RANGES_PRICES.dunham[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.dunham[0],
          medium: HANDLELESS_RANGE_PRICES.dunham[1],
          large: HANDLELESS_RANGE_PRICES.dunham[2],
          xlarge: HANDLELESS_RANGE_PRICES.dunham[3],
        },
        image: RANGES.dunham,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Nordic Nature',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Our new contemporary, Scandi-inspired concept, this stunning range sits on the cutting-edge of modern kitchen design.',
        priceBand: 3,
        colors: COLORS.nordicNature,
        prices: {
          small: RANGE_PRICES.nordicNature[0],
          medium: RANGE_PRICES.nordicNature[1],
          large: RANGE_PRICES.nordicNature[2],
          xlarge: RANGE_PRICES.nordicNature[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.nordicNature[0],
          medium: PREBUILT_RANGES_PRICES.nordicNature[1],
          large: PREBUILT_RANGES_PRICES.nordicNature[2],
          xlarge: PREBUILT_RANGES_PRICES.nordicNature[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.nordicNature[0],
          medium: HANDLELESS_RANGE_PRICES.nordicNature[1],
          large: HANDLELESS_RANGE_PRICES.nordicNature[2],
          xlarge: HANDLELESS_RANGE_PRICES.nordicNature[3],
        },
        image: RANGES.nordicNature,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Ludlow',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Our latest interpretation of the modern country-style kitchen. A matt painted shaker door, available in all Magnet Create shades.',
        priceBand: 3,
        colors: COLORS.ludlow,
        prices: {
          small: RANGE_PRICES.ludlow[0],
          medium: RANGE_PRICES.ludlow[1],
          large: RANGE_PRICES.ludlow[2],
          xlarge: RANGE_PRICES.ludlow[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.ludlow[0],
          medium: PREBUILT_RANGES_PRICES.ludlow[1],
          large: PREBUILT_RANGES_PRICES.ludlow[2],
          xlarge: PREBUILT_RANGES_PRICES.ludlow[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.ludlow[0],
          medium: HANDLELESS_RANGE_PRICES.ludlow[1],
          large: HANDLELESS_RANGE_PRICES.ludlow[2],
          xlarge: HANDLELESS_RANGE_PRICES.ludlow[3],
        },
        image: RANGES.ludlow,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Wardley',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'With a deep centre panel and painted timber finish, the narrow framing brings this classic kitchen style up to date.',
        priceBand: 3,
        colors: COLORS.wardley,
        prices: {
          small: RANGE_PRICES.wardley[0],
          medium: RANGE_PRICES.wardley[1],
          large: RANGE_PRICES.wardley[2],
          xlarge: RANGE_PRICES.wardley[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.wardley[0],
          medium: PREBUILT_RANGES_PRICES.wardley[1],
          large: PREBUILT_RANGES_PRICES.wardley[2],
          xlarge: PREBUILT_RANGES_PRICES.wardley[3],
        },
        handlelessPrices: {
          small: HANDLELESS_RANGE_PRICES.wardley[0],
          medium: HANDLELESS_RANGE_PRICES.wardley[1],
          large: HANDLELESS_RANGE_PRICES.wardley[2],
          xlarge: HANDLELESS_RANGE_PRICES.wardley[3],
        },
        image: RANGES.wardley,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_COMBINED,
      },
      {
        title: 'Bewdley',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'Every door is crafted from real timber, with delicate beading and mock in-frame detailing built directly into the design.',
        priceBand: 3,
        colors: COLORS.bewdley,
        prices: {
          small: PREBUILT_RANGES_PRICES.bewdley[0],
          medium: PREBUILT_RANGES_PRICES.bewdley[1],
          large: PREBUILT_RANGES_PRICES.bewdley[2],
          xlarge: PREBUILT_RANGES_PRICES.bewdley[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.bewdley[0],
          medium: PREBUILT_RANGES_PRICES.bewdley[1],
          large: PREBUILT_RANGES_PRICES.bewdley[2],
          xlarge: PREBUILT_RANGES_PRICES.bewdley[3],
        },
        image: RANGES.bewdley,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_WITH,
      },
      {
        title: 'Reed',
        collection: 'Magnet',
        ifcAvailable: true,
        description:
          'A contemporary fluted design with a smooth reverse, offering a striking textured finish that can be used across full kitchens or as a feature.',
        priceBand: 3,
        colors: COLORS.reed,
        prices: {
          small: PREBUILT_RANGES_PRICES.reed[0],
          medium: PREBUILT_RANGES_PRICES.reed[1],
          large: PREBUILT_RANGES_PRICES.reed[2],
          xlarge: PREBUILT_RANGES_PRICES.reed[3],
        },
        prebuiltPrices: {
          small: PREBUILT_RANGES_PRICES.reed[0],
          medium: PREBUILT_RANGES_PRICES.reed[1],
          large: PREBUILT_RANGES_PRICES.reed[2],
          xlarge: PREBUILT_RANGES_PRICES.reed[3],
        },
        image: RANGES.reed,
        showHandleSelection: true,
        handles: HANDLES_TYPE_VALUES_WITH,
      },
    ],
  },
  {
    id: 'cabinet',
    title: 'Would you like to buy flat-pack or pre-built?',
    shortTitle: 'Cabinet',
    type: 'cabinet',
    cards: [
      {
        title: 'Flat-pack',
        description: 'Easily assemble the frames and cabinets yourself.',
        imageSrc: CABINET.flatPack,
        type: 'flat-pack',
      },
      {
        title: 'Pre-built',
        description: 'Ready-to-use cabinets, expertly built to save you time.',
        imageSrc: CABINET.preBuilt,
        type: 'pre-built',
      },
    ],
  },
  {
    id: 'worktops',
    title: 'Which worktop style would you like?',
    shortTitle: 'Worktops',
    type: 'simple',
    description: 'Pick the style of worktop you’d like.',
    tooltip: {
      title: 'Which worktop style would you like?',
      description: [
        'Enhance your kitchen project with the latest new additions to our range of worktops.',
        'Choose from laminate, solid wood, minerva and more, to get an idea of pricing for your worktop material.',
      ],
    },
    cards: [
      {
        title: 'No worktop',
        description:
          'If you aren’t looking for your kitchen worktop right now, select this option.',
        prices: {
          small: 0,
          medium: 0,
          large: 0,
          xlarge: 0,
        },
      },
      {
        title: 'Bull nosed laminate',
        description:
          'Soften your kitchen’s edges with smooth rounded corners to your laminate worktops.',
        prices: {
          small: WORKTOP_PRICES.bullNosedLaminate[0],
          medium: WORKTOP_PRICES.bullNosedLaminate[1],
          large: WORKTOP_PRICES.bullNosedLaminate[2],
          xlarge: WORKTOP_PRICES.bullNosedLaminate[3],
        },
        image: WORKTOPS.bullNosedLaminate,
      },
      {
        title: 'Square edged laminate',
        description:
          'Angular laminate worktops create a kitchen with smart clean lines.',
        prices: {
          small: WORKTOP_PRICES.squareEdgedLaminate[0],
          medium: WORKTOP_PRICES.squareEdgedLaminate[1],
          large: WORKTOP_PRICES.squareEdgedLaminate[2],
          xlarge: WORKTOP_PRICES.squareEdgedLaminate[3],
        },
        image: WORKTOPS.squareEdgedLaminate,
      },
      {
        title: 'Compact Laminate',
        description:
          'Durable, water-resistant, and stylish - compact laminate worktops are visually minimal.',
        prices: {
          small: WORKTOP_PRICES.compactLaminate[0],
          medium: WORKTOP_PRICES.compactLaminate[1],
          large: WORKTOP_PRICES.compactLaminate[2],
          xlarge: WORKTOP_PRICES.compactLaminate[3],
        },
        image: WORKTOPS.compact,
      },
      {
        title: 'Timber',
        description:
          'Invite warm tones and organic woodgrain texture into your kitchen. ',
        prices: {
          small: WORKTOP_PRICES.timber[0],
          medium: WORKTOP_PRICES.timber[1],
          large: WORKTOP_PRICES.timber[2],
          xlarge: WORKTOP_PRICES.timber[3],
        },
        image: WORKTOPS.timber,
      },
      {
        title: 'Minerva',
        description:
          'Crafted with cutting-edge technology, Minerva worktops are luxurious and durable.',
        prices: {
          small: WORKTOP_PRICES.minerva[0],
          medium: WORKTOP_PRICES.minerva[1],
          large: WORKTOP_PRICES.minerva[2],
          xlarge: WORKTOP_PRICES.minerva[3],
        },
        image: WORKTOPS.minerva,
      },
      {
        title: 'Silestone',
        description:
          'Elevate your kitchen with Silestone’s crystal-like appearance and longevity.',
        prices: {
          small: WORKTOP_PRICES.silestone[0],
          medium: WORKTOP_PRICES.silestone[1],
          large: WORKTOP_PRICES.silestone[2],
          xlarge: WORKTOP_PRICES.silestone[3],
        },
        image: WORKTOPS.silestone,
      },
      {
        title: 'Dekton',
        description:
          'Incredibly hard wearing and versatile, Dekton blends porcelain, quartz and stone.',
        prices: {
          small: WORKTOP_PRICES.dekton[0],
          medium: WORKTOP_PRICES.dekton[1],
          large: WORKTOP_PRICES.dekton[2],
          xlarge: WORKTOP_PRICES.dekton[3],
        },
        image: WORKTOPS.dekton,
      },
      {
        title: 'Corian',
        description:
          'Blending natural quartz with man-made materials, Corian is durable yet elegant.',
        prices: {
          small: WORKTOP_PRICES.corian[0],
          medium: WORKTOP_PRICES.corian[1],
          large: WORKTOP_PRICES.corian[2],
          xlarge: WORKTOP_PRICES.corian[3],
        },
        image: WORKTOPS.corian,
      },
    ],
  },
  {
    id: 'installation',
    title: 'How will your kitchen be installed?',
    shortTitle: 'Installation',
    type: 'installation',
    description: 'Select the installation package you’d prefer.',
    tooltip: {
      title: 'How will your kitchen be installed?',
      description: [
        'Our expert kitchen installers will ensure your Magnet kitchen install runs as smoothly as possible.',
        'Alternatively, select ‘No installation’ if you’d prefer to use your own fitter or if you’d like our help with finding a local one in your area.',
        'Whichever you opt for, we’ll work closely with you and your fitter to ensure your kitchen is installed quickly, efficiently and to the best standard.',
      ],
    },
    cards: [
      {
        title: 'No installation',
        cardType: 0,
        description:
          'Use your own fitter, or we can help you find a reputable fitter in your area.',
        prices: {
          small: 0,
          medium: 0,
          large: 0,
          xlarge: 0,
        },
      },
      {
        title: 'Magnet dry-fit install',
        cardType: 1,
        description:
          'Our trusted installers assemble and fit your kitchen units, trims, panels, plinths, appliances (without connections), and laminate worktops.',
        prices: {
          small: INSTALLATION_PRICES.dryFitInstallation[0],
          medium: INSTALLATION_PRICES.dryFitInstallation[1],
          large: INSTALLATION_PRICES.dryFitInstallation[2],
          xlarge: INSTALLATION_PRICES.dryFitInstallation[3],
        },
      },
      {
        title: 'Magnet wet-fit install',
        cardType: 2,
        description:
          'Includes full dry-fit service plus connecting electrical appliances and plumbing.',
        prices: {
          small: INSTALLATION_PRICES.wetFitInstallation[0],
          medium: INSTALLATION_PRICES.wetFitInstallation[1],
          large: INSTALLATION_PRICES.wetFitInstallation[2],
          xlarge: INSTALLATION_PRICES.wetFitInstallation[3],
        },
      },
    ],
  },
  {
    id: 'flooring',
    title: 'Which kitchen flooring would you like?',
    shortTitle: 'Flooring',
    type: 'simple',
    description: 'Select your ideal choice of flooring. ',
    tooltip: {
      title: 'Which kitchen flooring would you like?',
      description: [
        'Find the perfect base for your kitchen project with our wide selection of flooring materials.',
        'Select from laminate, Amtico or engineered wood materials to pair perfectly with the rest of your kitchen.',
      ],
    },
    cards: [
      {
        title: 'No flooring',
        description:
          'If you aren’t looking for kitchen flooring right now, select this option.',
        price: 0,
      },
      {
        title: 'Laminate',
        description:
          'Ideal for replicating the look of natural wood or stone flooring, with all the practical benefits and affordability of laminate.',
        prices: {
          small: FLOORING_PRICES.laminate[0],
          medium: FLOORING_PRICES.laminate[1],
          large: FLOORING_PRICES.laminate[2],
          xlarge: FLOORING_PRICES.laminate[3],
        },
        image: FLOORING.laminate,
      },
      {
        title: 'Amtico',
        description:
          'Luxury vinyl flooring that spans wood, stone and abstract styles, designed and manufactured in the UK.',
        prices: {
          small: FLOORING_PRICES.amtico[0],
          medium: FLOORING_PRICES.amtico[1],
          large: FLOORING_PRICES.amtico[2],
          xlarge: FLOORING_PRICES.amtico[3],
        },
        image: FLOORING.amtico,
      },
      {
        title: 'Engineered',
        description:
          'Combining the natural beauty of solid wood flooring with added stability, our engineered flooring comes in a variety of specifications.',
        prices: {
          small: FLOORING_PRICES.engineered[0],
          medium: FLOORING_PRICES.engineered[1],
          large: FLOORING_PRICES.engineered[2],
          xlarge: FLOORING_PRICES.engineered[3],
        },
        image: FLOORING.engineered,
      },
    ],
  },
  {
    id: 'sinks',
    title: 'What style of kitchen sink would you like?',
    shortTitle: 'Sink',
    type: 'sinks',
    description: 'Choose the sink you’d like to add to your project.',
    tooltip: {
      title: 'What style of kitchen sink would you like?',
      description: [
        'With configurations to suit every kitchen and lifestyle, all of our sinks are durable, easy to clean and stylishly designed with modern living in mind.',
      ],
    },
    cards: [
      {
        title: 'Franke Ascona',
        description:
          'The Ascona from Franke is a classic stainless steel sink with a practical recessed drainer, and Slimfix system which removes the need for clamps, screws or fixing rails.',
        prices: [[SINK_PRICES.frankeAscona[0], SINK_PRICES.frankeAscona[1]]],
        sinks: SINKS_TYPE_VALUES,
        price: 256,
        images: [[SINKS.frankeAscona[0], SINKS.frankeAscona[1]]],
      },
      {
        title: 'Square',
        description:
          'A timeless design to match any style of kitchen. Add a square sink to your project and choose from a wide range of materials.',
        prices: [[SINK_PRICES.square[0], SINK_PRICES.square[1]]],
        sinks: SINKS_TYPE_VALUES,
        price: 275,
        images: [[SINKS.square[0], SINKS.square[1]]],
      },
      {
        title: 'Composite',
        description:
          'Combining hard-wearing stone and durable man made materials, choose a composite sink for ultimate durability to stand the test of time.',
        prices: [[SINK_PRICES.composite[0], SINK_PRICES.composite[1]]],
        sinks: SINKS_TYPE_VALUES,
        price: 287,
        images: [[SINKS.composite[0], SINKS.composite[1]]],
      },
      {
        title: 'Ceramic',
        description:
          'Add a touch of traditional charm with a ceramic sink. The perfect pairing for Shaker kitchens, our range of ceramic sinks can suit any space. ',
        prices: [[SINK_PRICES.ceramic[0], SINK_PRICES.ceramic[1]]],
        sinks: SINKS_TYPE_VALUES,
        price: 447,
        images: [[SINKS.ceramic[0], SINKS.ceramic[1]]],
      },
      {
        title: 'Belfast',
        description:
          'With a deep sink bowl and plenty of space, a Belfast sink is ideal for scrubbing large pots and pans. For practicality and style in equal measure, incorporate a Belfast sink.',
        prices: [[SINK_PRICES.belfast[0], SINK_PRICES.belfast[1]]],
        sinks: SINKS_TYPE_VALUES,
        price: 483,
        images: [[SINKS.belfast[0], SINKS.belfast[1]]],
      },
    ],
  },
  {
    id: 'taps',
    title: 'What type of kitchen tap would you like?',
    shortTitle: 'Tap',
    type: 'simple',
    description: 'Choose the tap you’d like to add to your project.',
    tooltip: {
      title: 'What type of kitchen tap would you like?',
      description: [
        'Our latest featured taps include designs with pull out spray features, mixer controls and instant boiling water taps to elevate your day-to-day life.',
        'Speak to a designer for a complete look at our full range of kitchen taps.',
      ],
    },
    cards: [
      {
        title: 'Neptune',
        description:
          'A classic silk steel monobloc mixer tap, with a swivelling spout for directional water flow.',
        price: 72,
        image: TAPS.neptune,
      },
      {
        title: 'Swan',
        description:
          'In a number of finishes including chrome, brass and brushed nickel, the Swan mixer tap adds Victorian-style elegance to your kitchen.',
        price: 139,
        image: TAPS.swan,
      },
      {
        title: 'Norrie',
        description:
          'Available in a range of finishes, the Norrie tap is a U-shaped contemporary brushed chrome tap with a swivelling spout for directional water flow.',
        price: 179,
        image: TAPS.norrie,
      },
      {
        title: 'Pico',
        description:
          'With options to suit every kitchen, choose Abode’s Pico tap for a simple, clean design that effortlessly coordinates with other kitchen accessories.',
        price: 214,
        image: TAPS.pico,
      },
      {
        title: 'Agilis',
        description:
          'Abode’s Agilis tap is a beautiful blend of design and functionality. An innovative front-facing handle allows easy waterflow control.',
        price: 391,
        image: TAPS.agilis,
      },
      {
        title: 'Hex Pull Out',
        description:
          'Eye-catching design makes a statement with Abode’s Hex tap. Redefining tap design, Hex embraces industrial elements.',
        price: 565,
        image: TAPS.hex,
      },
      {
        title: 'Pronteau by Abode',
        description:
          'The ProTrad 3in1 hot water tap brings Victorian elegance to your kitchen, delivering 98°C steaming hot water on demand.',
        price: 823,
        image: TAPS.pronteau,
      },
      {
        title: '3in1',
        description:
          'Pour cold, hot and boiling water all from one 3-in-1 tap. Plus, combine a Quooker tap with the CUBE attachment for chilled and sparkling water options.',
        price: 1250,
        image: TAPS.threeInOne,
      },
    ],
  },
  {
    id: 'storage',
    title: 'Would you like to add any kitchen storage?',
    shortTitle: 'Storage',
    type: 'storage',
    description:
      'Make your kitchen work for you with smart storage solutions that maximise space.',
    tooltip: {
      title: 'Would you like to add any kitchen storage?',
      description: [
        'Whether you’re looking to make better use of existing space, or create new functional areas of your kitchen, chat to one of our friendly designers who can talk you through our complete range of bespoke storage solutions.',
      ],
    },
    cards: [
      {
        title: 'Cutlery tray',
        description:
          'Elevate every detail with a timber cutlery tray that keeps utensils beautifully organised.',
        price: 45,
        image: STORAGE.cutleryTray,
        amount: 0,
      },
      {
        title: 'Tower pull out',
        description:
          'Keep your kitchen clutter-free with tower pull-out storage that maximises every inch of space.',
        price: 1046,
        image: STORAGE.towePullout,
        amount: 0,
      },
      {
        title: 'Traditional larder',
        description:
          'Add a traditional larder to your kitchen project and you’ll wonder how you ever lived without one.',
        price: 1953,
        image: STORAGE.traditionalLarder,
        amount: 0,
      },
    ],
  },
  {
    id: 'appliances',
    title: 'How many appliances would you like to add?',
    shortTitle: 'Appliances',
    type: 'appliances',
    description:
      'If you have an idea of the number of kitchen appliances you want, select here for a guide price.',
    tooltip: {
      title: 'How many appliances would you like to add?',
      description: [
        'Select your ideal brand and the number of appliances you’re looking for, and our calculator will offer your bespoke price estimate.',
        'For an in-depth look at the perfect appliance package for you, chat to our friendly kitchen designers.',
      ],
    },
    cards: [
      {
        title: 'No appliances',
        description:
          'If you aren’t looking for appliances right now, select this option.',
        price: 0,
      },
      {
        title: '3 appliances',
        description: 'Live better every day with three new appliances.',
        price: APPLIANCES_PRICES.beko[0],
        appliances: APPLIANCES_NAME_VALUES,
        prices: [
          [
            APPLIANCES_PRICES.beko[0],
            APPLIANCES_PRICES.beko[0],
            APPLIANCES_PRICES.bosch[0],
            APPLIANCES_PRICES.aeg[0],
            APPLIANCES_PRICES.neff[0],
            APPLIANCES_PRICES.smeg[0],
            APPLIANCES_PRICES.miele[0],
            APPLIANCES_PRICES.siemens[0],
          ],
        ],
      },
      {
        title: '4 appliances',
        description: 'Incorporate four appliances into your kitchen project.',
        price: APPLIANCES_PRICES.beko[1],
        appliances: APPLIANCES_NAME_VALUES,
        prices: [
          [
            APPLIANCES_PRICES.beko[1],
            APPLIANCES_PRICES.beko[1],
            APPLIANCES_PRICES.bosch[1],
            APPLIANCES_PRICES.aeg[1],
            APPLIANCES_PRICES.neff[1],
            APPLIANCES_PRICES.smeg[1],
            APPLIANCES_PRICES.miele[1],
            APPLIANCES_PRICES.siemens[1],
          ],
        ],
      },
      {
        title: '5 appliances',
        description: 'Elevate your kitchen with five new appliances.',
        price: APPLIANCES_PRICES.beko[2],
        appliances: APPLIANCES_NAME_VALUES,
        prices: [
          [
            APPLIANCES_PRICES.beko[2],
            APPLIANCES_PRICES.beko[2],
            APPLIANCES_PRICES.bosch[2],
            APPLIANCES_PRICES.aeg[2],
            APPLIANCES_PRICES.neff[2],
            APPLIANCES_PRICES.smeg[2],
            APPLIANCES_PRICES.miele[2],
            APPLIANCES_PRICES.siemens[2],
          ],
        ],
      },
      {
        title: '6 appliances',
        description: 'Make your kitchen a hub of modern technology.',
        price: APPLIANCES_PRICES.beko[3],
        appliances: APPLIANCES_NAME_VALUES_SMEG_EXCEPTION,
        prices: [
          [
            APPLIANCES_PRICES.beko[3],
            APPLIANCES_PRICES.beko[3],
            APPLIANCES_PRICES.bosch[3],
            APPLIANCES_PRICES.aeg[3],
            APPLIANCES_PRICES.neff[3],
            APPLIANCES_PRICES.miele[3],
            APPLIANCES_PRICES.siemens[3],
          ],
        ],
      },
    ],
  },
  {
    id: 'estimate',
    title: 'Estimate',
    type: 'summary',
    description: '',
    tooltip: {},
  },
]

export default sections
